import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { projectAPI, invoiceAPI, expenseAPI, vendorBillAPI, clientAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  ArrowLeft,
  Building2,
  Calendar,
  TrendingUp,
  TrendingDown,
  IndianRupee,
  FileText,
  Receipt,
  Wallet,
  BarChart3,
  Pencil,
  ExternalLink,
} from "lucide-react";

const statusColors = {
  active: "bg-emerald-100 text-emerald-700",
  completed: "bg-blue-100 text-blue-700",
  on_hold: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-700",
};

const statusLabels = {
  active: "Active",
  completed: "Completed",
  on_hold: "On Hold",
  cancelled: "Cancelled",
};

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [vendorBills, setVendorBills] = useState([]);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjectData();
  }, [id]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      const [projectRes, profitRes, invoicesRes, expensesRes, vendorBillsRes] = await Promise.all([
        projectAPI.get(id),
        projectAPI.getProfitability(id),
        invoiceAPI.list({ project_id: id }),
        expenseAPI.list({ project_id: id }),
        vendorBillAPI.list({ project_id: id }),
      ]);

      setProject(projectRes.data);
      setProfitability(profitRes.data);
      setInvoices(invoicesRes.data.invoices || []);
      setExpenses(expensesRes.data.expenses || []);
      setVendorBills(vendorBillsRes.data.vendor_bills || []);

      if (projectRes.data.client_id) {
        const clientRes = await clientAPI.get(projectRes.data.client_id);
        setClient(clientRes.data);
      }
    } catch (error) {
      toast.error("Failed to load project details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
          <p className="text-slate-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-slate-600">Project not found</p>
        <Button variant="outline" onClick={() => navigate("/projects")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Button>
      </div>
    );
  }

  const profit = profitability || {};
  const marginColor = (profit.profit_margin || 0) >= 0 ? "text-emerald-600" : "text-red-600";

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/projects")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">{project.name}</h1>
              <Badge className={statusColors[project.status]}>
                {statusLabels[project.status]}
              </Badge>
            </div>
            {project.code && (
              <p className="text-slate-500 mt-1">Code: {project.code}</p>
            )}
          </div>
        </div>
        <Button onClick={() => navigate(`/projects/${id}/edit`)} data-testid="edit-project-btn">
          <Pencil className="w-4 h-4 mr-2" />
          Edit Project
        </Button>
      </div>

      {/* Project Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Client
            </CardTitle>
          </CardHeader>
          <CardContent>
            {client ? (
              <Link
                to={`/clients/${client.id}`}
                className="font-medium text-blue-600 hover:underline flex items-center gap-1"
              >
                {client.name}
                <ExternalLink className="w-3 h-3" />
              </Link>
            ) : (
              <span className="text-slate-400">No client linked</span>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Duration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">
              {formatDate(project.start_date)} - {formatDate(project.end_date)}
            </p>
            {project.city && (
              <p className="text-sm text-slate-500 mt-1">
                {project.city}{project.state ? `, ${project.state}` : ""}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Budget
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(project.budget)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Profitability Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Project Profitability
          </CardTitle>
          <CardDescription>Financial summary for this project</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div className="space-y-1">
              <p className="text-sm text-slate-500 flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                Revenue
              </p>
              <p className="text-xl font-bold text-emerald-600">
                {formatCurrency(profit.total_revenue)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-slate-500 flex items-center gap-1">
                <TrendingDown className="w-4 h-4 text-red-500" />
                Costs
              </p>
              <p className="text-xl font-bold text-red-600">
                {formatCurrency(profit.total_cost)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-slate-500">Gross Profit</p>
              <p className={`text-xl font-bold ${(profit.gross_profit || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {formatCurrency(profit.gross_profit)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-slate-500">Net Profit</p>
              <p className={`text-xl font-bold ${(profit.net_profit || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {formatCurrency(profit.net_profit)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-slate-500">Margin %</p>
              <p className={`text-xl font-bold ${marginColor}`}>
                {(profit.profit_margin || 0).toFixed(1)}%
              </p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="space-y-1">
              <p className="text-sm text-slate-500 flex items-center gap-1">
                <IndianRupee className="w-4 h-4" />
                Received
              </p>
              <p className="text-lg font-semibold text-slate-900">
                {formatCurrency(profit.amount_received)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-slate-500">Outstanding</p>
              <p className="text-lg font-semibold text-amber-600">
                {formatCurrency(profit.amount_outstanding)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-slate-500">Vendor Bills</p>
              <p className="text-lg font-semibold text-slate-900">
                {formatCurrency(profit.vendor_bill_total)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-slate-500">Expenses</p>
              <p className="text-lg font-semibold text-slate-900">
                {formatCurrency(profit.expense_total)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Invoices, Vendor Bills, Expenses */}
      <Tabs defaultValue="invoices" className="w-full">
        <TabsList>
          <TabsTrigger value="invoices" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Invoices ({invoices.length})
          </TabsTrigger>
          <TabsTrigger value="vendor-bills" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Vendor Bills ({vendorBills.length})
          </TabsTrigger>
          <TabsTrigger value="expenses" className="flex items-center gap-2">
            <Wallet className="w-4 h-4" />
            Expenses ({expenses.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invoices">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Project Invoices</CardTitle>
                <CardDescription>All invoices linked to this project</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/invoices/new?project_id=${id}`)}
              >
                Create Invoice
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        No invoices linked to this project
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <Link
                            to={`/invoices/${invoice.id}`}
                            className="text-blue-600 hover:underline font-medium"
                          >
                            {invoice.invoice_number}
                          </Link>
                        </TableCell>
                        <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {invoice.status?.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(invoice.grand_total)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(invoice.balance_due)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vendor-bills">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Vendor Bills</CardTitle>
                <CardDescription>All vendor bills linked to this project</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/vendor-bills/new?project_id=${id}`)}
              >
                Add Vendor Bill
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bill #</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendorBills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        No vendor bills linked to this project
                      </TableCell>
                    </TableRow>
                  ) : (
                    vendorBills.map((bill) => (
                      <TableRow key={bill.id}>
                        <TableCell className="font-medium">{bill.bill_number}</TableCell>
                        <TableCell>{bill.vendor_name || "-"}</TableCell>
                        <TableCell>{formatDate(bill.bill_date)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {bill.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(bill.grand_total)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expenses">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Expenses</CardTitle>
                <CardDescription>All expenses linked to this project</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/expenses/new?project_id=${id}`)}
              >
                Add Expense
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Expense #</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        No expenses linked to this project
                      </TableCell>
                    </TableRow>
                  ) : (
                    expenses.map((expense) => (
                      <TableRow key={expense.id}>
                        <TableCell className="font-medium">{expense.expense_number}</TableCell>
                        <TableCell>{expense.description}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {expense.category?.replace("_", " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(expense.expense_date)}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(expense.amount)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Project Description */}
      {project.description && (
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 whitespace-pre-wrap">{project.description}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
